import { useState, useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { User as UserIcon } from 'lucide-react';
import { cn, getFlagEmoji } from '@/lib/utils';
import { computeDnfStats, DNF_HIGH_THRESHOLD } from '@/lib/dnf';
import { FORMAT_BADGE_STYLES } from '@/lib/constants';
import { RunnerStatsModal } from '@/components/runners/RunnerStatsModal';
import { useTranslation } from 'react-i18next';

interface RunnerActionSheetProps {
    runner: {
        name?: string | null;
        nationality?: string | null;
        pictureUri?: string | null;
        utmbIndex?: number | null;
        dnfRatio?: number | null;
        longestFinishedRaceKm?: number | null;
        formatStats?: Array<{ format?: string | null; index?: number | null; finishedRaces?: number | null }> | null;
        utmbUri?: string | null;
        effectiveIndex?: number | null;
        [key: string]: unknown;
    };
    raceFormat?: string | null;
    expectedRank?: number;
    potentialScore?: number;
    onClose: () => void;
    children: ReactNode;
}

export function RunnerActionSheet({ runner, raceFormat, expectedRank, potentialScore, onClose, children }: RunnerActionSheetProps) {
    const { t } = useTranslation('races');
    const [showFullStats, setShowFullStats] = useState(false);
    const [isDesktop, setIsDesktop] = useState(() => typeof window !== 'undefined' && window.matchMedia('(min-width: 768px)').matches);

    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = ''; };
    }, []);

    useEffect(() => {
        const mq = window.matchMedia('(min-width: 768px)');
        const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
        mq.addEventListener('change', handler);
        return () => mq.removeEventListener('change', handler);
    }, []);

    const fmtStat = runner.formatStats?.find(s => s.format === raceFormat);

    const dnfStats = computeDnfStats(runner.dnfRatio, runner.formatStats);
    const dnfIsHigh = dnfStats != null && dnfStats.ratio > DNF_HIGH_THRESHOLD;

    return (
        <>
            {createPortal(
                <div className="fixed inset-0 z-[60] flex items-end md:items-center md:justify-center">
                    <div className="absolute inset-0 bg-slate-900/20 backdrop-blur-[2px]" onClick={() => { onClose(); setShowFullStats(false); }} />
                    <div className={cn(
                        "relative bg-white shadow-2xl max-h-[85vh] overflow-y-auto",
                        isDesktop
                            ? "rounded-3xl w-full max-w-sm border border-slate-100 animate-in zoom-in-95 duration-200"
                            : "rounded-t-3xl w-full border-t border-slate-100 pb-safe animate-in slide-in-from-bottom duration-200"
                    )}>
                        {/* Handle (mobile only) */}
                        {!isDesktop && <div className="flex justify-center pt-3 pb-1"><div className="w-10 h-1 rounded-full bg-slate-200" /></div>}

                        {/* Runner header */}
                        <div className={cn("px-5 pb-3 flex items-center gap-3", isDesktop ? "pt-6" : "pt-1")}>
                            <div className="w-11 h-11 rounded-xl overflow-hidden bg-slate-100 border border-slate-100 flex-shrink-0">
                                {runner.pictureUri ? (
                                    <img src={runner.pictureUri} className="w-full h-full object-cover" alt="" />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center text-slate-300">
                                        <UserIcon size={20} />
                                    </div>
                                )}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-1.5">
                                    <h3 className="text-base font-bold text-slate-900 truncate">{runner.name}</h3>
                                    <span className="text-base flex-shrink-0">{getFlagEmoji(runner.nationality)}</span>
                                </div>
                                <div className="flex items-center gap-2 mt-0.5">
                                    <span className="text-xs font-bold text-slate-500">UTMB {Math.round(runner.utmbIndex || 0)}</span>
                                    {raceFormat && FORMAT_BADGE_STYLES[raceFormat] && (
                                        <span
                                            className="px-1.5 py-0.5 rounded text-[10px] font-black"
                                            style={{
                                                backgroundColor: FORMAT_BADGE_STYLES[raceFormat].bg,
                                                color: FORMAT_BADGE_STYLES[raceFormat].text,
                                            }}
                                        >
                                            {FORMAT_BADGE_STYLES[raceFormat].label}
                                        </span>
                                    )}
                                </div>
                            </div>
                            {potentialScore != null && (
                                <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-indigo-50 rounded-xl flex-shrink-0">
                                    <span className="text-sm font-black text-indigo-700">+{Math.round(potentialScore)}</span>
                                </div>
                            )}
                        </div>

                        {/* Stats grid */}
                        <div className="px-5 pb-2 grid grid-cols-4 gap-1.5 text-center">
                            {expectedRank != null && (
                                <div className="bg-slate-50 rounded-lg py-2">
                                    <span className="block text-sm font-black text-slate-800">#{expectedRank}</span>
                                    <span className="text-[10px] text-slate-400">Rang att.</span>
                                </div>
                            )}
                            <div className="bg-slate-50 rounded-lg py-2">
                                <span className={cn("block text-sm font-black", dnfIsHigh ? "text-red-600" : "text-slate-800")}>{dnfStats != null ? `${dnfStats.dnfCount}/${dnfStats.totalRaces}` : '—'}</span>
                                <span className="text-[10px] text-slate-400">DNF</span>
                            </div>
                            <div className="bg-slate-50 rounded-lg py-2">
                                <span className={cn("block text-sm font-black", (fmtStat?.finishedRaces ?? 0) === 0 ? "text-amber-600" : "text-slate-800")}>
                                    {fmtStat?.finishedRaces ?? 0}
                                </span>
                                <span className="text-[10px] text-slate-400">Courses</span>
                            </div>
                            <div className="bg-slate-50 rounded-lg py-2">
                                <span className="block text-sm font-black text-slate-800">{runner.longestFinishedRaceKm != null ? `${Math.round(runner.longestFinishedRaceKm)}` : '—'}</span>
                                <span className="text-[10px] text-slate-400">Max km</span>
                            </div>
                        </div>

                        {/* Stats detail link */}
                        <div className="px-5 pb-2">
                            <button
                                onClick={() => setShowFullStats(true)}
                                className="w-full flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-semibold text-emerald-600 hover:bg-emerald-50 transition-all"
                            >
                                {t('details.runnerStats.viewDetails')} →
                            </button>
                        </div>

                        {/* Separator */}
                        <div className="mx-5 border-t border-slate-100" />

                        {/* Actions slot */}
                        <div className="px-5 py-3 space-y-3">
                            {children}
                        </div>
                    </div>
                </div>,
                document.body,
            )}

            {showFullStats && (
                <RunnerStatsModal
                    runner={runner as Parameters<typeof RunnerStatsModal>[0]['runner']}
                    raceFormat={raceFormat}
                    onClose={() => setShowFullStats(false)}
                />
            )}
        </>
    );
}
